import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Receipt as ReceiptIcon,
  Home as HomeIcon,
  AccountBalance as AccountBalanceIcon,
  ExpandMore as ExpandMoreIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ownerCommissionService from '../../services/ownerCommissionService';
import OverduePayinsSection from './OverduePayinsSection';

const CommissionPaymentSuccessPage = () => {
  const { t } = useTranslation('financial');
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState(null);
  const [payinsDetails, setPayinsDetails] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Extraire l'invoice_id de l'URL
  const searchParams = new URLSearchParams(location.search);
  const invoiceId = searchParams.get('invoice_id');

  useEffect(() => {
    const fetchInvoiceDetails = async () => {
      if (!invoiceId) {
        setError(t('commissionPaymentSuccess.notFound'));
        setLoading(false);
        return;
      }

      try {
        // Appel API pour récupérer les détails de la facture
        const data = await ownerCommissionService.getInvoiceDetails(invoiceId);
        setInvoice(data);
      } catch (err) {
        console.error('Erreur lors de la récupération de la facture:', err);
        setError(t('commissionPaymentSuccess.error'));
      } finally {
        setLoading(false);
      }
    };

    fetchInvoiceDetails();
  }, [invoiceId]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  };

  const formatAmount = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  // Charger les détails des payins
  const loadPayinsDetails = async (page = 1, reset = false) => {
    if (loadingDetails || !invoiceId) return;

    setLoadingDetails(true);
    try {
      const data = await ownerCommissionService.getInvoicePayinsDetails(invoiceId, {
        page: page,
        limit: 10
      });

      if (reset) {
        setPayinsDetails(data.payins);
      } else {
        setPayinsDetails(prev => [...prev, ...data.payins]);
      }
      
      setHasMore(data.pagination.hasMore);
      setCurrentPage(page);
    } catch (err) {
      console.error('Erreur lors de la récupération des détails des payins:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Charger plus de données
  const handleLoadMore = () => {
    if (hasMore && !loadingDetails) {
      loadPayinsDetails(currentPage + 1, false);
    }
  };

  // Télécharger le PDF de la facture
  const handleDownloadPdf = async () => {
    if (!invoiceId || downloadingPdf) return;

    setDownloadingPdf(true);
    try {
      console.log('Début du téléchargement PDF pour la facture:', invoiceId);
      
      const pdfBlob = await ownerCommissionService.downloadInvoicePdf(invoiceId);
      
      // Vérifier que le blob est valide
      if (!pdfBlob || pdfBlob.size === 0) {
        throw new Error('Le fichier PDF reçu est vide');
      }

      console.log('PDF reçu, taille:', pdfBlob.size, 'bytes');
      
      // Vérifier le type MIME
      if (pdfBlob.type && !pdfBlob.type.includes('pdf')) {
        console.warn('Type MIME inattendu:', pdfBlob.type);
      }
      
      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facture-commission-${invoice?.invoice_number || invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      console.log('Téléchargement PDF terminé avec succès');
    } catch (err) {
      console.error('Erreur lors du téléchargement du PDF:', err);
      // Vous pouvez ajouter ici une notification d'erreur si nécessaire
      alert('Erreur lors du téléchargement du PDF. Veuillez réessayer.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
          <Typography variant="body1" sx={{ ml: 2 }}>
            {t('commissionPaymentSuccess.loading')}
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => navigate('/dashboard')}
          startIcon={<HomeIcon />}
        >
          {t('commissionPaymentSuccess.actions.backToDashboard')}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* En-tête de succès */}
      <Card sx={{ mb: 3, bgcolor: 'success.light', color: 'success.contrastText' }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircleIcon sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h4" component="h1" gutterBottom>
            {t('commissionPaymentSuccess.title')}
          </Typography>
          <Typography variant="h6">
            {t('commissionPaymentSuccess.subtitle')}
          </Typography>
        </CardContent>
      </Card>

      {/* Détails de la facture */}
      {invoice && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={3}>
              <ReceiptIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h5" component="h2">
                {t('commissionPaymentSuccess.invoiceDetails')}
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('commissionPaymentSuccess.invoiceNumber')}
              </Typography>
              <Typography variant="h6" gutterBottom>
                {invoice.invoice_number}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="body1">
                {t('commissionPaymentSuccess.period')}
              </Typography>
              <Typography variant="body1">
                {formatDate(invoice.period_start)} - {formatDate(invoice.period_end)}
              </Typography>
            </Box>

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="body1">
                {t('commissionPaymentSuccess.totalAmount')}
              </Typography>
              <Typography variant="h6" color="primary.main">
                {formatAmount(invoice.total_amount, invoice.currency)}
              </Typography>
            </Box>

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="body1">
                {t('commissionPaymentSuccess.status')}
              </Typography>
              <Chip 
                label={t('commissionPaymentSuccess.paid')} 
                color="success" 
                variant="filled"
                size="small"
              />
            </Box>

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="body1">
                {t('commissionPaymentSuccess.paymentDate')}
              </Typography>
              <Typography variant="body1">
                {invoice.paid_date ? formatDate(invoice.paid_date) : t('commissionPaymentSuccess.today')}
              </Typography>
            </Box>

            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body1">
                {t('commissionPaymentSuccess.paymentMethod')}
              </Typography>
              <Typography variant="body1">
                {t('commissionPaymentSuccess.cardPayment')}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Accordéon avec détails des réservations */}
      {invoice && (
        <Card sx={{ mb: 3 }}>
          <Accordion 
            defaultExpanded={false}
            onChange={(event, isExpanded) => {
              if (isExpanded && payinsDetails.length === 0) {
                loadPayinsDetails(1, true);
              }
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                {t('commissionPaymentSuccess.reservationDetails')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {invoice?.metadata?.invoice_type === 'overdue_recovery' && (
                <OverduePayinsSection payins={payinsDetails} />
              )}
              {invoice?.metadata?.invoice_type !== 'overdue_recovery' && payinsDetails.map((payin, index) => (
                <Card key={payin.id} sx={{ mb: 2, bgcolor: 'grey.50' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="primary">
                      {payin.reservation.listing.title}
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">
                          {t('commissionPaymentSuccess.reservationPeriod')}
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {formatDate(payin.reservation.start_date)} - {formatDate(payin.reservation.end_date)}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">
                          {t('commissionPaymentSuccess.cashReceived')}
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {formatAmount(payin.cash_amount_received, payin.original_currency)}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">
                          {t('commissionPaymentSuccess.commissionRate')}
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {payin.commission_rate_percentage}%
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">
                          {t('commissionPaymentSuccess.commissionAmount')}
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {formatAmount(payin.commission_amount, payin.original_currency)}
                        </Typography>
                      </Grid>
                      
                      {payin.taxes && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            {t('commissionPaymentSuccess.taxes')}
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            {formatAmount(payin.taxes, payin.original_currency)}
                          </Typography>
                        </Grid>
                      )}
                      
                      {payin.original_currency && payin.original_currency !== payin.currency ? (
                        <>
                          <Grid item xs={12}>
                            <Divider sx={{ my: 0.5 }} />
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">
                              {t('commissionPaymentSuccess.commissionConverted', 'Montant commission')}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                              {payin.converted_amount_with_currency || formatAmount(payin.converted_amount, payin.currency)}
                            </Typography>
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">
                              {t('commissionPaymentSuccess.taxesConverted', 'Taxes converties')}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                              {formatAmount(
                                Number(
                                  ((payin.exchange_rate ? payin.taxes / payin.exchange_rate : payin.taxes) || 0).toFixed(2)
                                ),
                                payin.currency
                              )}
                            </Typography>
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">
                              {t('commissionPaymentSuccess.totalConverted', 'Total dû converti')}
                            </Typography>
                            <Typography variant="body1" gutterBottom sx={{ fontWeight: 700 }}>
                              {formatAmount(
                                Number((payin.converted_amount || 0)) +
                                  Number(((payin.exchange_rate ? payin.taxes / payin.exchange_rate : payin.taxes) || 0).toFixed(2)),
                                payin.currency
                              )}
                            </Typography>
                          </Grid>

                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              {t('commissionPaymentSuccess.exchangeRateExplanation')} (1 {payin.currency} = {payin.exchange_rate} {payin.original_currency})
                            </Typography>
                          </Grid>
                        </>
                      ) : (
                        <>
                          <Grid item xs={12}>
                            <Divider sx={{ my: 0.5 }} />
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">
                              {t('invoiceDetail.totalCommissionTaxesEuros', 'Montant total dû (commission + taxes) en EUR')}
                            </Typography>
                            <Typography variant="body1" gutterBottom sx={{ fontWeight: 700 }}>
                              {formatAmount(
                                Number((payin.commission_amount || 0)) + Number((payin.taxes || 0)),
                                'EUR'
                              )}
                            </Typography>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              ))}
              
              {/* Bouton Load More */}
              {hasMore && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Button
                    variant="outlined"
                    onClick={handleLoadMore}
                    disabled={loadingDetails}
                    startIcon={loadingDetails ? <CircularProgress size={20} /> : null}
                  >
                    {loadingDetails ? t('commissionPaymentSuccess.loading') : t('commissionPaymentSuccess.loadMore')}
                  </Button>
                </Box>
              )}
              
              {!hasMore && payinsDetails.length > 0 && (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
                  {t('commissionPaymentSuccess.noMoreData')}
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        </Card>
      )}

      {/* Actions */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('commissionPaymentSuccess.nextSteps')}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {t('commissionPaymentSuccess.confirmationMessage')}
          </Typography>
          
          <Box display="flex" gap={2} flexWrap="wrap">
            <Button
              variant="contained"
              onClick={() => navigate('/dashboard')}
              startIcon={<HomeIcon />}
            >
              {t('commissionPaymentSuccess.actions.backToDashboard')}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/financial-owner')}
              startIcon={<AccountBalanceIcon />}
            >
              {t('commissionPaymentSuccess.actions.viewAllInvoices')}
            </Button>
            <Button
              variant="outlined"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              startIcon={downloadingPdf ? <CircularProgress size={20} /> : <DownloadIcon />}
              sx={{ 
                color: downloadingPdf ? 'text.disabled' : 'primary.main',
                borderColor: downloadingPdf ? 'action.disabled' : 'primary.main'
              }}
            >
              {downloadingPdf 
                ? t('commissionPaymentSuccess.actions.downloadingReceipt')
                : t('commissionPaymentSuccess.actions.downloadReceipt')
              }
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default CommissionPaymentSuccessPage;